import { anyEntityId } from '@pkg/db/schema'
import type { SyncServerDatabase } from '@pkg/sync/common/schema'
import type { Transaction } from 'kysely'
import { z } from 'zod'
import { hlcString } from './hlc'

export const SyncMessageStatus = {
  applied: 'applied',
  rejected: 'rejected',
} as const

export type SyncMessageStatusType = (typeof SyncMessageStatus)[keyof typeof SyncMessageStatus]

export const SyncMessageSchema = z.object({
  intentTs: hlcString, // Hybrid Logical Clock (HLC)
  transactionTs: hlcString,
  userId: z.string(),
  row: anyEntityId,
  column: z.string(),
  value: z.any(),
})

export type SyncMessage = z.infer<typeof SyncMessageSchema>

export const SyncRequestSchema = z.object({
  nodeId: z.string(),
  userId: z.string(),
  clientTime: z.iso.datetime(),
  lastSyncTs: z.string().nullish(),
  messages: z.array(SyncMessageSchema.omit({ userId: true })),
})

export type SyncRequest = z.infer<typeof SyncRequestSchema>

export const ClientSyncRequest = SyncRequestSchema.omit({ userId: true })

export type ClientSyncRequest = z.infer<typeof ClientSyncRequest>

export const SyncResponseSchema = z.object({
  messages: z.array(SyncMessageSchema), // MessageTable rows from server
  newSyncTs: z.string(),
  hasMore: z.boolean(),
})

export type SyncResponse = z.infer<typeof SyncResponseSchema>

export type Vote = 'apply' | 'reject' | 'abstain'

export interface UpdateVoterParams<DB extends SyncServerDatabase> {
  trx: Transaction<DB>
  msg: SyncMessage
  currentIntentTs?: string
  currentTransactionTs?: string
}

export interface SyncUpdateVoter<DB extends SyncServerDatabase> {
  vote(params: UpdateVoterParams<DB>): Promise<Vote>
}

export interface SyncMessageProcessorParams<DB> {
  trx: Transaction<DB>
  msg: SyncMessage
  status: SyncMessageStatusType
  enqueueAfterCommit: (fn: () => Promise<void> | void) => void
}

export interface SyncMessageProcessor<DB> {
  process(params: SyncMessageProcessorParams<DB>): Promise<void> | void
  onBatchEnd?(params: { trx: Transaction<DB> }): Promise<void>
}
